import { EnvelopeIcon } from "@heroicons/react/24/outline";
import { formatDateNoTime, InboundLeadEmailId, relativeTimeAgo } from "@sp-crm/core";
import { InlineBanner } from "components/ui/inline-banner";
import { RetryableErrorBanner } from "components/ui/retryable-error-banner";
import { Spinner } from "components/ui/spinner";
import { ConversationFeedBasicsFragment } from "generated/graphql";
import React, { useCallback } from "react";
import { isoDateStringToHumanReadable } from "util/date/locale-date-format";
import { getSanitizedMarkUpFromInboundLeadEmail } from "util/email";
import { handleEvent } from "util/user-events";
import { selectedThreadRowClass, threadRowClass } from "./conversation-feed-utils";
import { messageBodyErrorText } from "./message-body-error";
import { useLeadSourceEmailBodyQuery } from "./use-message-body";

export type LeadSourceEmailSummary = NonNullable<
    ConversationFeedBasicsFragment["threads"][number]["leadSourceEmail"]
>;

const NO_SUBJECT = "(no subject)";
const UNKNOWN_SENDER = "an unknown sender";
const UNKNOWN_SENDER_NAME = "Unknown";
const UNAVAILABLE_MESSAGE =
    "This email is no longer stored, so its content can't be shown.";

export const leadSourceEmailSubject = (email: LeadSourceEmailSummary): string =>
    email.subject || NO_SUBJECT;

const senderOf = (email: LeadSourceEmailSummary): string =>
    email.fromAddress || UNKNOWN_SENDER;

const senderNameOf = (email: LeadSourceEmailSummary): string =>
    email.fromAddress || UNKNOWN_SENDER_NAME;

interface LeadSourceEmailListItemProps {
    email: LeadSourceEmailSummary;
    selected: boolean;
    onSelect: () => void;
}

export const LeadSourceEmailListItem: React.FC<LeadSourceEmailListItemProps> = props => {
    const { email, selected, onSelect } = props;
    const select = useCallback(() => {
        handleEvent("lead-source-email-open");
        onSelect();
    }, [onSelect]);

    return (
        <li
            className={selected ? selectedThreadRowClass : threadRowClass}
            onClick={select}>
            <EnvelopeIcon
                className="h-3.5 w-3.5 text-gray-400 flex-shrink-0 mt-1"
                aria-hidden="true"
            />
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                    <span className="font-medium text-sm text-gray-900 truncate">
                        {leadSourceEmailSubject(email)}
                    </span>
                    <span className="text-[11px] tabular-nums text-gray-400 ml-auto flex-shrink-0">
                        {formatDateNoTime(email.receivedAt)}
                    </span>
                </div>
                <div className="text-xs text-gray-500 truncate">
                    {email.sourceName} lead source · {senderOf(email)}
                </div>
            </div>
        </li>
    );
};

interface LeadSourceEmailBodyProps {
    id: InboundLeadEmailId;
}

const LeadSourceEmailBody: React.FC<LeadSourceEmailBodyProps> = props => {
    const query = useLeadSourceEmailBodyQuery(props.id);

    if (query.isError) {
        return (
            <RetryableErrorBanner
                message={messageBodyErrorText(query.error)}
                onRetry={() => query.refetch()}
                retrying={query.isFetching}
            />
        );
    }
    if (!query.isSuccess) {
        return <Spinner />;
    }
    const body = query.data;
    return body.kind === "available" ? (
        <div
            className="unreset message-body text-sm text-gray-700"
            dangerouslySetInnerHTML={getSanitizedMarkUpFromInboundLeadEmail(body.html)}
        />
    ) : (
        <InlineBanner type="info">{UNAVAILABLE_MESSAGE}</InlineBanner>
    );
};

interface LeadSourceEmailReadPaneProps {
    email: LeadSourceEmailSummary;
}

export const LeadSourceEmailReadPane: React.FC<LeadSourceEmailReadPaneProps> = props => {
    const { email } = props;
    const senderName = senderNameOf(email);

    return (
        <div className="flex-1 min-h-0 flex flex-col">
            <header className="flex-none border-b border-gray-200 bg-white px-5 py-3.5">
                <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-wider font-medium text-gray-500 min-w-0 mb-1.5">
                    <EnvelopeIcon className="h-3 w-3 flex-shrink-0" aria-hidden="true" />
                    <span className="truncate">Email</span>
                    <span aria-hidden="true" className="text-gray-300">
                        ·
                    </span>
                    <span className="truncate">{email.sourceName} lead source</span>
                </div>
                <h2 className="text-base font-semibold text-gray-900 leading-snug truncate">
                    {leadSourceEmailSubject(email)}
                </h2>
                <div className="flex items-center gap-2 text-xs text-gray-500 mt-1 min-w-0">
                    <span className="truncate">from {senderOf(email)}</span>
                </div>
            </header>
            <ul role="list" className="flex-1 min-h-0 overflow-y-auto py-2">
                <li className="border-b border-gray-200">
                    <div className="flex items-start gap-3 px-5 py-2">
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                                <span
                                    className="font-medium text-sm text-gray-900 min-w-0 truncate"
                                    title={senderName}>
                                    {senderName}
                                </span>
                                <span className="text-xs text-gray-500 flex-shrink-0">
                                    {relativeTimeAgo(new Date(email.receivedAt))}
                                </span>
                            </div>
                            <div className="text-sm text-gray-500">
                                <span className="text-gray-400">Lead source:</span>{" "}
                                {email.sourceName}
                            </div>
                            <div className="text-sm text-gray-500">
                                <span className="text-gray-400">Received:</span>{" "}
                                {isoDateStringToHumanReadable(email.receivedAt)}
                            </div>
                        </div>
                    </div>
                    <div className="pt-2 pb-3 px-5">
                        <LeadSourceEmailBody id={email.id} />
                    </div>
                </li>
            </ul>
        </div>
    );
};
